import { useEffect, useState } from 'react';

// Path-routing primitive for the SPA. Reads `location.pathname`, writes via
// `history.pushState`, listens to `popstate` for back/forward.
//
// Higher-level matching/parsing lives in `parsePath` and the per-workspace
// route hooks (`useFleetRoute`).

export interface BrowserRoute {
  path: string;
  setPath: (next: string) => void;
}

// `pushState` fires no global event, so consumers of this hook would drift
// apart when one navigates. The custom event fans the change out to every
// active subscriber.
const PATH_CHANGE_EVENT = 'fleet:path-change';

function readPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

export function useBrowserRoute(): BrowserRoute {
  const [path, setPathState] = useState<string>(readPath);

  useEffect(() => {
    const onChange = () => setPathState(readPath());
    window.addEventListener('popstate', onChange);
    window.addEventListener(PATH_CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener(PATH_CHANGE_EVENT, onChange);
    };
  }, []);

  const setPath = (next: string): void => {
    if (window.location.pathname !== next) {
      window.history.pushState(null, '', next);
      window.dispatchEvent(new Event(PATH_CHANGE_EVENT));
    }
  };

  return { path, setPath };
}
